import * as React from 'react';
import classNames from 'classnames';
import RModal from 'react-modal';
import styles from './Modal.module.scss';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';

interface ModalProps {
    body?: React.ReactNode;
    bodyClassName?: string;
    className?: string;
    contentLabel: string;
    footer?: React.ReactNode;
    footerClassName?: string;
    header?: React.ReactNode;
    headerClassName?: string;
    large?: boolean;
    leftImage?: React.ReactNode;
    medium?: boolean;
    modalRightClassName?: string;
    modalSize?: string;
    shouldReturnFocusAfterClose?: boolean;
    show?: boolean;
    small?: boolean;
}

const Modal = (props: ModalProps) => {
    const {
        body,
        bodyClassName,
        className,
        contentLabel,
        footer,
        footerClassName,
        header,
        headerClassName,
        large = false,
        leftImage,
        medium = false,
        modalRightClassName,
        modalSize = '',
        shouldReturnFocusAfterClose = true,
        show = false,
        small = false,
    } = props;

    const isMobile = useIsNarrowerThan(768);

    function onShow() {
        const scrollY = window.pageYOffset;
        document.body.style.top = `-${scrollY}px`;
        document.body.style.position = 'fixed';
        document.body.style.overflow = 'auto';
        document.body.style.paddingRight = isMobile ? '' : '15px';
    }

    function onHide() {
        const scrollY = document.body.style.top;
        document.body.style.position = '';
        document.body.style.overflow = '';
        document.body.style.top = '';
        document.body.style.paddingRight = '';
        window.scrollTo(0, parseInt(scrollY, 10) * -1);
    }

    const classes = classNames(
        className,
        styles['modal-content'],
        { [styles['modal-content-large']]: large },
        { [styles['modal-content-medium']]: medium },
        { [styles['modal-content-small']]: small },
        { [modalSize]: modalSize },
        { [styles['modal-content-size']]: !large && !small && !modalSize }
    );

    const rightModalClasses = classNames(styles['modal-right'], modalRightClassName);
    const footerClasses = classNames(styles['modal-footer'], footerClassName);
    const bodyClasses = classNames(styles['modal-body'], bodyClassName || styles['modal-body-default']);
    const headerClasses = classNames(styles['modal-header'], headerClassName || styles['modal-header-default']);

    return (
        <RModal
            ariaHideApp
            className={{
                afterOpen: styles['modal-content--after-open'] || '',
                base: classes,
                beforeClose: styles['modal-content--before-close'] || '',
            }}
            closeTimeoutMS={100}
            contentLabel={contentLabel}
            isOpen={show}
            onAfterClose={onHide}
            onAfterOpen={onShow}
            overlayClassName={{
                afterOpen: styles['modal-overlay--after-open'] || '',
                base: `${styles['modal-overlay']}`,
                beforeClose: styles['modal-overlay--before-close'] || '',
            }}
            shouldFocusAfterRender
            shouldReturnFocusAfterClose={shouldReturnFocusAfterClose}
        >
            {leftImage && <div className={styles['modal-leftImage']}>{leftImage}</div>}
            <div className={rightModalClasses}>
                {header && <div className={headerClasses}>{header}</div>}
                {body && <div className={bodyClasses}>{body}</div>}
                {footer && <div className={footerClasses}>{footer}</div>}
            </div>
        </RModal>
    );
};

export default Modal;
